import { currencyFormatter, sumValues } from "../../../shared/utils/formatters";

const rootStyles = getComputedStyle(document.documentElement);

export const earningsColour = rootStyles
  .getPropertyValue("--earnings-color")
  .trim();
export const preReformLegacyColour = rootStyles
  .getPropertyValue("--pre-reform-legacy-color")
  .trim();
export const preReformRemedyColour = rootStyles
  .getPropertyValue("--pre-reform-remedy-color")
  .trim();
export const postReformLegacyColour = rootStyles
  .getPropertyValue("--post-reform-legacy-color")
  .trim();
export const postReformRemedyColour = rootStyles
  .getPropertyValue("--post-reform-remedy-color")
  .trim();
export const lumpSumColour = rootStyles
  .getPropertyValue("--lump-sum-color")
  .trim();
export const fontColor = rootStyles.getPropertyValue("--font-color").trim();

export const titleFontSize = 17;
export const normalFontSize = 14;

export const defaultChartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  animation: true,
  plugins: {
    legend: {
      display: true,
      position: "bottom",
      align: "center",
      labels: {
        boxWidth: 20,
        font: {
          size: 14,
        },
        color: fontColor,
      },
      onClick: null,
    },
    title: {
      display: true,
      font: {
        weight: "bold",
        size: titleFontSize,
      },
      padding: {
        bottom: 2,
      },
      color: fontColor,
    },
    subtitle: {
      display: true,
      font: {
        size: normalFontSize,
      },
      padding: {
        bottom: 16,
      },
      color: fontColor,
    },
    tooltip: {
      interaction: {
        intersect: false,
      },
      backgroundColor: "white",
      borderColor: "black",
      borderWidth: 1,
      titleColor: fontColor,
      titleFont: {
        size: normalFontSize,
      },
      bodyColor: fontColor,
      bodyFont: {
        size: normalFontSize,
      },
      yAlign: "center",
      callbacks: {
        title: function (context) {
          return context[0].label.replaceAll(",", " ");
        },
        label: function (context) {
          let label = context.dataset.label || "";
          if (label) {
            label += ": ";
          }
          if (context.parsed.y !== null) {
            label += currencyFormatter(context.parsed.y);
          }
          return label;
        },
      },
    },
    datalabels: {
      anchor: "end",
      align: "top",
      offset: -3,
      formatter: (value, context) => {
        if (context.datasetIndex === context.chart.data.datasets.length - 1) {
          const total = context.chart.data.datasets.reduce((acc, dataset) => {
            return acc + dataset.data[context.dataIndex];
          }, 0);
          return currencyFormatter(total);
        } else {
          return "";
        }
      },
      font: {
        size: normalFontSize,
      },
      color: fontColor,
    },
  },
  scales: {
    y: {
      title: {
        display: true,
        font: {
          size: normalFontSize * 1.1,
        },
        color: fontColor,
      },
      afterFit: function (scaleInstance) {
        scaleInstance.width = 72;
      },
      stacked: true,
      grid: { display: false },
      ticks: {
        callback: function (value) {
          return currencyFormatter(value);
        },
        font: {
          size: normalFontSize,
        },
        color: fontColor,
      },
      beginAtZero: true,
    },
    x: {
      stacked: true,
      grid: { display: false },
      ticks: {
        autoSkip: false,
        font: {
          size: normalFontSize,
        },
        color: fontColor,
      },
    },
  },
};

export const getMaxScaleValue = (data, type) => {
  const roundUpToNearestFiveThousand = (value) => {
    return Math.ceil((value + 2000) / 5000) * 5000;
  };

  if (type === "income") {
    let maxIncome = {
      partialLegacy: 0,
      partialReform: 0,
      fullLegacy: 0,
      fullReform: 0,
    };

    let earningsMap = {
      partialLegacy: 0,
      partialReform: 0,
      fullLegacy: 0,
      fullReform: 0,
    };

    data.forEach((item) => {
      const values = item.values || {};

      if (item.benefitType === "earnings") {
        if (
          item.remedyChoice === "legacy" &&
          item.retirementType === "partial"
        ) {
          earningsMap.partialLegacy = values.earnings || 0;
        } else if (
          item.remedyChoice === "reform" &&
          item.retirementType === "partial"
        ) {
          earningsMap.partialReform = values.earnings || 0;
        } else if (
          item.remedyChoice === "legacy" &&
          item.retirementType === "full"
        ) {
          earningsMap.fullLegacy = values.earnings || 0;
        } else if (
          item.remedyChoice === "reform" &&
          item.retirementType === "full"
        ) {
          earningsMap.fullReform = values.earnings || 0;
        }
      }

      if (item.benefitType === "pension") {
        if (
          item.remedyChoice === "legacy" &&
          item.retirementType === "partial"
        ) {
          maxIncome.partialLegacy =
            earningsMap.partialLegacy +
            (values.legacy || 0) +
            (values.reform || 0);
        } else if (
          item.remedyChoice === "reform" &&
          item.retirementType === "partial"
        ) {
          maxIncome.partialReform =
            earningsMap.partialReform +
            (values.legacy || 0) +
            (values.reform || 0);
        } else if (
          item.remedyChoice === "legacy" &&
          item.retirementType === "full"
        ) {
          maxIncome.fullLegacy =
            earningsMap.fullLegacy +
            (values.legacy || 0) +
            (values.reform || 0);
        } else if (
          item.remedyChoice === "reform" &&
          item.retirementType === "full"
        ) {
          maxIncome.fullReform =
            earningsMap.fullReform +
            (values.legacy || 0) +
            (values.reform || 0);
        }
      }
    });

    const highestIncome = Math.max(
      maxIncome.partialLegacy,
      maxIncome.partialReform,
      maxIncome.fullLegacy,
      maxIncome.fullReform
    );

    return roundUpToNearestFiveThousand(highestIncome);
  } else if (type === "lump sum") {
    let maxLumpSum = {
      partialLegacy: 0,
      partialReform: 0,
      fullLegacy: 0,
      fullReform: 0,
    };

    data.forEach((item) => {
      const values = item.values || {};

      if (item.benefitType === "lump_sum") {
        if (
          item.remedyChoice === "legacy" &&
          item.retirementType === "partial"
        ) {
          maxLumpSum.partialLegacy = sumValues(values) || 0;
        } else if (
          item.remedyChoice === "reform" &&
          item.retirementType === "partial"
        ) {
          maxLumpSum.partialReform = sumValues(values) || 0;
        } else if (
          item.remedyChoice === "legacy" &&
          item.retirementType === "full"
        ) {
          maxLumpSum.fullLegacy = sumValues(values) || 0;
        } else if (
          item.remedyChoice === "reform" &&
          item.retirementType === "full"
        ) {
          maxLumpSum.fullReform = sumValues(values) || 0;
        }
      }
    });

    const highestLumpSum = Math.max(
      maxLumpSum.partialLegacy,
      maxLumpSum.partialReform,
      maxLumpSum.fullLegacy,
      maxLumpSum.fullReform
    );

    return roundUpToNearestFiveThousand(highestLumpSum);
  }

  return null;
};
